import React, { useEffect, useState, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, RadialBarChart, RadialBar } from "recharts";
import config from "../config";

export default function Dashboard() {
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [bpmData, setBpmData] = useState([]);
  const [predData, setPredData] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filters, setFilters] = useState({ BPM: false, Pred: false, ECG: false });
  const [searchTerm, setSearchTerm] = useState("");
  const [ecgId, setEcgId] = useState("");
  const [bpmId, setBpmId] = useState("");
  const [predId, setPredId] = useState("");
  const [prevEcgId, setPrevEcgId] = useState("");
  const [prevBpmId, setPrevBpmId] = useState("");
  const [prevPredId, setPrevPredId] = useState("");
  const intervalRef = useRef(null);
  const sampleRate = 125;

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [ecgRecords, setEcgRecords] = useState([]);
  const [bpmRecords, setBpmRecords] = useState([]);
  const [predRecords, setPredRecords] = useState([]);

  const [chartWidth, setChartWidth] = useState(Math.max(window.innerWidth * 0.9, 300));

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const chartSize = width < 768 ? width - 40 : width * 0.9;
      setChartWidth(Math.max(chartSize, 300));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async (id, type, setDataCallback) => {
    try {
      let url;
      if (config.isProduction) {
        url = `${config.apiBaseUrl}?endpoint=registro/info&id=${id}&type=${type}`;
      } else {
        url = `${config.apiBaseUrl}/registro/info?id=${id}&type=${type}`;
      }
      const response = await fetch(url, {
        headers: config.isProduction ? {} : { 'X-API-Key': config.apiKey },
      });
      const result = await response.json();
      const formattedData = result.map((value, index) => ({
        time: type === "BPM" ? index : index / sampleRate,
        value,
      }));
      setDataCallback(formattedData);
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
    }
  };

  const fetchPredData = async (id) => {
    try {
      let url;
      if (config.isProduction) {
        url = `${config.apiBaseUrl}?endpoint=registro/info&id=${id}&type=Pred`;
      } else {
        url = `${config.apiBaseUrl}/registro/info?id=${id}&type=Pred`;
      }
      const response = await fetch(url, {
        headers: config.isProduction ? {} : { 'X-API-Key': config.apiKey },
      });
      const result = await response.json();
      const colors = {"!": "#8884d8", "F": "#82ca9d", "N": "#ffc658", "V": "#ff7300", "f": "#d0ed57"};
      const formattedPredData = Object.keys(result).map((key) => ({ name: key, value: result[key], fill: colors[key] || "#cccccc" }));
      setPredData(formattedPredData);
    } catch (error) {
      console.error("Error fetching Pred data:", error);
    }
  };

  const updateFilteredRecordsByType = (records) => {
    const uniqueUsers = [...new Set(records.map(record => record.full_name))];
    setUsers(uniqueUsers);
    setEcgRecords(records.filter(record => record.type === 'ECG'));
    setBpmRecords(records.filter(record => record.type === 'BPM'));
    setPredRecords(records.filter(record => record.type === 'Pred'));
  };

  const fetchRecords = async () => {
    try {
      let url;
      if (config.isProduction) {
        url = `${config.apiBaseUrl}?endpoint=listar_registros/info`;
      } else {
        url = `${config.apiBaseUrl}/listar_registros/info`;
      }
      const response = await fetch(url, {
        headers: config.isProduction ? {} : { 'X-API-Key': config.apiKey },
      });
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse JSON response:", text.substring(0, 200));
        throw new Error("API returned invalid JSON");
      }
      setRecords(result);
      setFilteredRecords(result);
      updateFilteredRecordsByType(result);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  const handleFetchData = () => {
    if (ecgId !== prevEcgId) {
      fetchData(ecgId, "ECG", (formattedData) => {
        setData(formattedData);
        setDisplayData(formattedData.slice(0, 1250));
        setElapsedTime(0);
      });
      setPrevEcgId(ecgId);
    }
    if (bpmId !== prevBpmId) {
      fetchData(bpmId, "BPM", setBpmData);
      setPrevBpmId(bpmId);
    }
    if (predId !== prevPredId) {
      fetchPredData(predId);
      setPrevPredId(predId);
    }
    fetchRecords();
  };

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setElapsedTime((prevTime) => {
          const newIndex = Math.floor(prevTime * sampleRate) + sampleRate;
          setDisplayData(data.slice(newIndex, newIndex + 1250));
          return prevTime + 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, data, sampleRate]);

  useEffect(() => {
    const active = Object.keys(filters).filter((k) => filters[k]);
    let filtered = records;
    if (active.length > 0) filtered = filtered.filter((r) => active.includes(r.type));
    if (searchTerm) filtered = filtered.filter((r) => r.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredRecords(filtered);
  }, [filters, searchTerm, records]);

  useEffect(() => { fetchRecords(); }, []);
  useEffect(() => {
    if (selectedUser) {
      const byUser = records.filter(r => r.full_name === selectedUser);
      updateFilteredRecordsByType(byUser);
    } else {
      updateFilteredRecordsByType(records);
    }
  }, [selectedUser, records]);

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  const toggleFilter = (type) => setFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const formatTime = (timeInSeconds) => {
    const ms = Math.floor((timeInSeconds % 1) * 1000);
    const seconds = Math.floor(timeInSeconds % 60);
    const minutes = Math.floor((timeInSeconds / 60) % 60);
    const hours = Math.floor(timeInSeconds / 3600);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
  };

  const totalDuration = data.length / sampleRate;
  const remainingTime = totalDuration - elapsedTime;

  return (
    <div className="kb-container">
      <section className="kb-card">
        <h2 className="kb-title">Lista de Registros</h2>
        <div className="kb-filter-row">
          <button className={filters.BPM ? "kb-chip active" : "kb-chip"} onClick={() => toggleFilter("BPM")}>{filters.BPM ? "Ocultar BPM" : "Mostrar BPM"}</button>
          <button className={filters.Pred ? "kb-chip active" : "kb-chip"} onClick={() => toggleFilter("Pred")}>{filters.Pred ? "Ocultar Pred" : "Mostrar Pred"}</button>
          <button className={filters.ECG ? "kb-chip active" : "kb-chip"} onClick={() => toggleFilter("ECG")}>{filters.ECG ? "Ocultar ECG" : "Mostrar ECG"}</button>
        </div>
        <input className="kb-input" type="text" placeholder="Buscar por nombre" value={searchTerm} onChange={handleSearchChange} />
        <div className="kb-table-wrapper">
          <table className="kb-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Tipo</th>
                <th>Tamaño</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={record._id}>
                  <td>{record._id}</td>
                  <td>{record.full_name}</td>
                  <td>{record.email}</td>
                  <td>{record.created_date}</td>
                  <td>{record.created_time}</td>
                  <td>{record.type}</td>
                  <td>{record.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="kb-card">
        <h2 className="kb-title">Consultas Dinámicas</h2>
        <div className="kb-grid-3">
          <div>
            <label className="kb-label">Filtrar por usuario</label>
            <select className="kb-input" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
              <option value="">Todos los usuarios</option>
              {users.map(user => (<option key={user} value={user}>{user}</option>))}
            </select>
          </div>
          <div>
            <label className="kb-label">ID ECG</label>
            <select className="kb-input" value={ecgId} onChange={(e) => setEcgId(e.target.value)}>
              <option value="">Seleccionar ECG</option>
              {ecgRecords.map(record => (<option key={record._id} value={record._id}>{record.full_name} - {record._id}</option>))}
            </select>
          </div>
          <div>
            <label className="kb-label">ID BPM</label>
            <select className="kb-input" value={bpmId} onChange={(e) => setBpmId(e.target.value)}>
              <option value="">Seleccionar BPM</option>
              {bpmRecords.map(record => (<option key={record._id} value={record._id}>{record.full_name} - {record._id}</option>))}
            </select>
          </div>
          <div>
            <label className="kb-label">ID Pred</label>
            <select className="kb-input" value={predId} onChange={(e) => setPredId(e.target.value)}>
              <option value="">Seleccionar Pred</option>
              {predRecords.map(record => (<option key={record._id} value={record._id}>{record.full_name} - {record._id}</option>))}
            </select>
          </div>
        </div>
        <button className="kb-btn" onClick={handleFetchData}>Consultar</button>
      </section>

      <section className="kb-card">
        <h2 className="kb-title">Gráfico ECG</h2>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <LineChart width={chartWidth} height={window.innerWidth < 768 ? 300 : 500} data={displayData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" label={{ value: 'Time (s)', position: 'insideBottomRight', offset: -10 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="value" stroke="#4F46E5" dot={false} />
          </LineChart>
        </div>
        <div className="kb-timer-row">
          <button className="kb-btn" onClick={handlePlayPause}>{isPlaying ? "Pause" : "Play"}</button>
          <span className="kb-badge">Transcurrido: {formatTime(elapsedTime)}</span>
          <span className="kb-badge">Restante: {formatTime(remainingTime)}</span>
        </div>
      </section>

      <section className="kb-grid-2">
        <div className="kb-card">
          <h3 className="kb-title">BPM</h3>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <BarChart width={chartWidth} height={window.innerWidth < 768 ? 200 : 250} data={bpmData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" label={{ value: 'Time (min)', position: 'insideBottomRight', offset: -10 }} domain={[0, bpmData.length - 1]} ticks={[0, bpmData.length - 1]} tickFormatter={(tick) => (tick / 60).toFixed(2)} />
              <YAxis />
              <Tooltip formatter={(value, name, props) => [`${value}`, `Time: ${(props.payload.time / 60).toFixed(2)} min`]} />
              <Legend />
              <Bar dataKey="value" fill="#22C55E" />
            </BarChart>
          </div>
        </div>
        <div className="kb-card">
          <h3 className="kb-title">Predicciones</h3>
          <div style={{ width: '100%', overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
            <RadialBarChart width={Math.min(610, chartWidth)} height={window.innerWidth < 768 ? 300 : 500} cx={Math.min(300, chartWidth/2)} cy={window.innerWidth < 768 ? 140 : 220} innerRadius={30} outerRadius={Math.min(220, chartWidth/2.5)} barSize={20} data={predData}>
              <RadialBar minAngle={15} label={{ position: 'insideStart', fill: '#fff' }} background clockWise dataKey="value" isAnimationActive={false} />
              <Legend iconSize={10} layout="vertical" verticalAlign="middle" wrapperStyle={{ top: 0, left: Math.min(550, chartWidth - 60), lineHeight: '24px' }} payload={predData.map((entry) => ({ value: `${entry.name}: ${entry.value}`, type: "square", id: entry.name, color: entry.fill }))} />
              <Tooltip />
            </RadialBarChart>
          </div>
        </div>
      </section>
    </div>
  );
}
